import {formatDate} from "../utils.js";
import {isEmpty} from "../utils.js";


export class Waitlist {
    constructor() {
        this.waitlist = new Map();
    }

    composeKeyToWaitFor(waitingForClass, waitingForId) {
        return `${waitingForClass.name}.${waitingForId}`;
    }

    addToWaitlist(waitingForClass, waitingForId, instanceThatIsWaiting, assignToAttribute) {
        const outerKey = this.composeKeyToWaitFor(waitingForClass, waitingForId);
        if (!this.waitlist.has(outerKey)) {
            this.waitlist.set(outerKey, []);
        }
        const innerMap = this.waitlist.get(outerKey);
        innerMap.push([instanceThatIsWaiting, assignToAttribute]);
    }

    getWaitingList(playerThree) {
        const outerKey = this.composeKeyToWaitFor(playerThree.constructor, playerThree.id);
        return this.waitlist.get(outerKey) || [];
    }

    handleWaitlist(playerThree) {  //Has entered the game.
        /**
         * If a new model instance enters the playing field, pass it here as playerTree (entered the game!) and those
         * models that are waiting for that model will have it resolved/assigned at the correct attribute as a foreign
         * key.
         */
        const outerKey = this.composeKeyToWaitFor(playerThree.constructor, playerThree.id);
        const waitlist = this.waitlist.get(outerKey);
        if (!waitlist) {
            return;
        }
        for (const [instanceThatIsWaiting, assignToAttribute] of waitlist) {
            instanceThatIsWaiting[assignToAttribute] = playerThree;
            if (!playerThree[instanceThatIsWaiting.constructor.pluralName]) {
                playerThree[instanceThatIsWaiting.constructor.pluralName] = new Set();
            }
            playerThree[instanceThatIsWaiting.constructor.pluralName].add(instanceThatIsWaiting);
            instanceThatIsWaiting.resolveLocalForeignKeys(); // marks _fkResolved if all FKs are now resolved
        }
        this.waitlist.set(outerKey, []); // Clear the waitlist for this key
    }
}

export class Model {
    /*
    The Model class is intended to be extended like in Django's ORM. Subclasses
    must define the following STATIC variables.

      - uri: The URI on the DAL endpoint from which to fetch models. This
             should include no additional parameters, and start/end with /
      - columns: The names of the columns on the django database model you
             would like to pull into the frontend. This will request ONLY those
             parameters, and is intended to be cumbersome to prevent you from
             arbitrarily pulling all fields.
      - pluralName: A plural name for this model. Only required if this model is
             referenced by a weed-js.models.ForeignKey somewhere.
      - foreignKeys: A lookup table of foreign keys. The key should be the name
             you would like to use on the local model for the foreign model. For
             example, if you're defining that a Product model has a foreign key
             to a Category model that references the Category.id from the
             Product.category_id then your foreignKeys would be defined on the
             Product.foreignKeys static attribute and look like this:

             class Product extends Model {

                static foreignKeys = {
                    category: new ForeignKey(Category)
                }

                ...other attributes and whatnot...
             }

             When you then call instantiate a model (after
             data is pulled using the relevant methods (fetch, fetchByDate) on
             both the Product and Category models) the Product.category field
             will be populated with the appropriate Category instance matching
             the category_id on the Product model.

             Note that we always assume the column name on the local model
             matches the key defined in the foreignKeys attribute with an "_id"
             appended. I.e. in the above definition we assume the local field
             that references the Category is category_id, which is the key
             "category" with the suffix "_id" appended. We always assume the
             field matched on the foreign model is "id", which is our internal
             convention.
      - dateField: We require that you pass a dateField, which
             is a parameter that defines the time from which we should begin and end
             pulling models of the type you're defining. For example, for
             the Transaction model representing a retail transaction we define
             dateField: "transaction_date".

             Note that you're allowed to use django's join functionality
             when you define these methods. For example, if you want to match
             transaction Items on their transaction's transaction_date you
             can set dateField: "transaction__transaction_date" since
             the Item references the Transaction through a foreign key.

             The dateField is used when we
             call the backend APIs to canonicalize caching. We cache
             request/responses by date range.

     */
    static waitlist = new Waitlist();
    static _uncanonicalized = new Set();

    // If the model has a canonical model, set it here so it can be created alongside this model
    static canonicalClasses = null

    static get byId() {
        if (!Object.prototype.hasOwnProperty.call(this, "_byId")) {
            this._byId = new Map();
        }
        return this._byId;
    }

    static init() {
        if (!this.hasOwnProperty("uri")) {
            this.uri = "/";
        }
        if (!this.hasOwnProperty("columns")) {
            this.columns = [];
        }
        if (!this.hasOwnProperty("pluralName")) {
            this.pluralName = "";
        }
        if (!this.hasOwnProperty("foreignKeys")) {
            this.foreignKeys = {};
        }
        if (!this.hasOwnProperty("dateField")) {
            this.dateField = null; // Defaults to null, meaning no start date param
        }
        if (!this.hasOwnProperty("_foreignKeySpecs")) {
            this._foreignKeySpecs = Object.entries(this.foreignKeys).map(([name, fk]) => ({
                name,
                idKey: name + "_id",
                foreignModel: fk.foreignModel,
            }));
        }
    }


    constructor(values) {
        const className = this.constructor.name;
        if (values === undefined || values === null) {
            throw new Error("You must pass a values object to the Model constructor. The argument you passed to instantiate a `" + className + "` was " + values);
        }
        this.constructor.init()
        this._modelName = this.constructor.name;

        // When values is an array, create it from the projection:
        if (Array.isArray(values)) {
            for (let i = 0; i < values.length; i++) {
                this[this.constructor.columns[i]] = values[i];
            }
        } else { // When values is an object, use the defined fields to extract those that are relevant:
            this.constructor.columns.map(column => {
                if (column in values) {
                    this[column] = values[column];
                } else {
                    this[column] = null; // Set to null if the value is not present
                }
            });
        }

        if (this.id === undefined) {
            throw new Error("You must define an 'id' field on the " + className + " model. The values you passed were: " + JSON.stringify(values));
        }
        if (this.id === null) {
            console.warn("Not resolving foreign keys for a model without an ID. This model should only be used to create a new instance, and then discarded. Do not re-use this model, use the one that is returned in the create/save/createOrUpdate promise instead. The model you instantiated was: " + JSON.stringify(values));
            return;
        }

        // Make a copy of the foreign key specs for the class so instances can modify them:
        this.foreignKeySpecs = [...this.constructor._foreignKeySpecs];

        if (this.constructor.byId.get(this.id)) {
            throw new Error("You have attempted to instantiate a " + className + " with an ID that already exists. You should use the static fetch methods on the class to get existing instances. The ID you attempted to use was: " + this.id);
        }
        this.constructor.updateInstances([this]);

        if (!isEmpty(this.constructor.canonicalClasses) && !this.isCanonical) {
            Model._uncanonicalized.add(this);
        }

        const inst = this.constructor.byId.get(this.id);
        inst.resolveLocalForeignKeys();
        Model.waitlist.handleWaitlist(this);
    }

    _fkResolved() {
        return this.foreignKeySpecs.length === 0;
    }

    static shuffle(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1)); // random index from 0 to i
            [array[i], array[j]] = [array[j], array[i]];   // swap
        }
        return array;
    }

    static canonicalize(lastSize=null) {
        return new Promise((resolve, reject) => {
            try {
                const incomplete = [];
                for (const instance of Model._uncanonicalized) {
                    if (instance.isCanonical) continue;
                    if (!instance._fkResolved()) instance.resolveLocalForeignKeys();
                    if (instance._fkResolved()) Model.waitlist.handleWaitlist(instance);

                    const canonicalClasses = instance.constructor.canonicalClasses || [];

                    if (isEmpty(canonicalClasses)) {
                        instance.isCanonical = true;
                        continue;
                    }

                    if (instance?.canonical === undefined) {
                        instance.canonical = canonicalClasses?.reduce((acc, cls) => {
                            if (!cls || !cls?.name) {
                                console.log("Why would we have an undefined canonical class?", cls, acc, instance, instance.constructor, canonicalClasses);
                            }
                            acc[cls.name] = null;
                            return acc;
                        }, {}) || {};
                    }

                    try {
                        for (let i = 0; i < (canonicalClasses?.length || 0); i++) {
                            const canonicalClass = canonicalClasses[i];
                            if (instance.canonical[canonicalClass.name]?.isCanonical) continue;
                            instance.canonical[canonicalClass.name] = canonicalClass.from(instance);
                            instance.canonical[canonicalClass.name].origin = instance;
                        }
                        instance.isCanonical = true;
                    } catch (e) {
                        if (Math.random() > 0.9999) {
                            console.warn("Could not canonicalize instance", instance, "due to error:", e);
                        }
                        incomplete.push(instance);
                    }
                }

                Model._uncanonicalized = new Set(Model.shuffle(incomplete));
                if (Model._uncanonicalized.size !== 0) {
                    if (Model._uncanonicalized.size === lastSize) {
                        console.warn("Could not canonicalize all instances after multiple attempts. Remaining uncanonicalized instances:", Model._uncanonicalized);
                        return resolve(Model._uncanonicalized);
                    }
                    return this.canonicalize(Model._uncanonicalized.size).then(resolve).catch(reject);
                }
                return resolve(Model._uncanonicalized);
            } catch (err) {
                console.error("Error during canonicalization:", err);
                return reject(err);
            }
        });
    }

    resolveLocalForeignKeys() {
        if (this._fkResolved()) return;

        const toReattempt = [];
        while (this.foreignKeySpecs.length > 0) {
            const {name, idKey, foreignModel} = this.foreignKeySpecs.shift();
            if (this[name] !== undefined) { // || this[name]?.constructor instanceof Model) {
                continue; // Already resolved.
            }
            if (!this[idKey]) {
                continue; // No foreign ID to resolve.
            }

            const byId = foreignModel.byId;
            const foreignId = this[idKey];
            const relatedInstance = byId?.get(foreignId);

            if (!isEmpty(relatedInstance)) {
                if (!relatedInstance._fkResolved()) {
                    relatedInstance.resolveLocalForeignKeys();
                    Model.waitlist.handleWaitlist(relatedInstance);
                }
                this[name] = relatedInstance;
                const set = relatedInstance[this.constructor.pluralName] ||= new Set();
                if (!set.has(this)) set.add(this);
            } else {
                toReattempt.push({name, idKey, foreignModel});
                Model.waitlist.addToWaitlist(
                    foreignModel,
                    foreignId,
                    this,
                    name
                )
            }
        }
        this.foreignKeySpecs = toReattempt;
    }

    // Base classes should define a getParams() method as follows:
    // static getParams(startDate, endDate, accounts) {
    //     return {};
    // }

    create(client) {
        const payload = this.constructor.columns.reduce((pl, column) => {
            if (column === "id") {
                return pl;
            }
            pl[column] = this[column];
            return pl;
        }, {});
        return this.constructor.dal(client).post(this.constructor.uri, payload).then(response => {
            if (response === null || response === undefined) {
                throw new Error("Response from the DAL was null or undefined when creating " + this.constructor.toString());
            }
            this.id = response.data.id; // Set the ID from the response
            this.constructor.updateInstances([this]);
            return this;
        });
    }

    save(client) {
        const payload = this.constructor.columns.reduce((pl, column) => {
            if (column === "id") {
                return pl;
            }
            pl[column] = this[column];
            return pl;
        }, {});
        return this.constructor.dal(client).patch(this.constructor.uri + `${this.id}/`, payload).then(response => {
            if (response === null || response === undefined) {
                throw new Error("Response from the DAL was null or undefined when creating " + this.constructor.toString());
            }
            this.constructor.updateInstances([this]);
            return this;
        });
    }

    createOrUpdate(client) {
        if (this.id) {
            return this.save(client);
        }
        return this.create(client);
    }

    static validate() {
        const errors = [];
        if (this.uri === "/") {
            errors.push("You must define a (String) uri on " + this.toString() +
                " as a static variable");
        }
        if (this.columns.length === 0) {
            errors.push("You must define (Array[String]) database columns to" +
                " pull from the API as a static Array variable on " +
                this.toString() + " called 'columns'.");
        }
        if (this.foreignKeys.length > 0 && this.pluralName === "") {
            errors.push("You must define a (String) pluralName as a static" +
                " variable on " + this.toString() + " when you" +
                " define foreign keys so that other models know how" +
                " to reference it.");
        }
        return errors;
    }

    static getBaseParams({startDate, endDate, ...overrides}) {
        const baseParams = {};
        if (this.dateField !== null && this.dateField !== undefined) {
            baseParams[this.dateField + "__gte"] = formatDate(startDate);
            baseParams[this.dateField + "__lte"] = formatDate(endDate);
        }
        return {
            ...baseParams,
            proj: this.columns.join(","),
            flat: true,
            limit: overrides?.limit || 1000000000,
            _cb: 2,
        };
    }

    static getParams({startDate, endDate, ...overrides}) {
        /*
        You can optionally define a getParams method to add additional params,
        but by default we already include the start date param, the end date
        param, and the projection you have defined on the class.
         */
        return this.getBaseParams({startDate, endDate, ...overrides});
    }

    static _getParams({startDate, endDate, ...overrides}) {
        const userParams = this.getParams({startDate, endDate, ...overrides});
        const baseParams = this.getBaseParams({startDate, endDate, ...overrides});
        return {
            ...baseParams,
            ...userParams,
        };
    }

    static getId(columns, values) {
        if (!isNaN(this._primaryKeyIndex)) {
            return values[this._primaryKeyIndex];
        }
        for (let i=0; i < columns.length; i++) {
            if (columns[i] === "id") {
                this._primaryKeyIndex = i;
                return values[i];
            }
        }
        throw new Error("Could not find 'id' in columns: " + JSON.stringify(columns));
    }

    static updateFlatInstances(instances) {
        const instancesAsModels = [];
        for (const row of instances) {
            const id = this.getId(this.columns, row);
            const existing = this.byId.get(id);
            if (isEmpty(existing)) {
                const instance = new this(row);
                this.byId.set(instance.id, instance);
                instancesAsModels.push(instance);
            } else {
                instancesAsModels.push(existing);
            }
        }
        return instancesAsModels;
    }

    static updateClassInstances(instances) {
        const instancesAsModels = [];
        for (const instance of instances) {
            // If it's not an instance of the class already, construct it
            if (!instance.id) {
                throw new Error("You must pass an instance with an 'id' field to the updateInstances method on " + this.toString() + ". The instance you passed was: " + JSON.stringify(instance));
            }
            const existing = this.byId.get(instance.id);
            if (isEmpty(existing)) {
                this.byId.set(instance.id, instance);
            }
            instancesAsModels.push(this.byId.get(instance.id));
        }
        return instancesAsModels;
    }

    static updateMapInstances(instances) {
        const instancesAsModels = [];
        for (const instance of instances) {
            // If it's not an instance of the class already, construct it
            if (!instance.id) {
                throw new Error("You must pass an instance with an 'id' field to the updateInstances method on " + this.toString() + ". The instance you passed was: " + JSON.stringify(instance));
            }
            const existing = this.byId.get(instance.id);
            if (isEmpty(existing)) {
                this.byId.set(instance.id, new this(instance));
            }
            instancesAsModels.push(this.byId.get(instance.id));
        }
        return instancesAsModels;
    }

    static updateInstances(instances) {
        if (isEmpty(instances)) {
            return [];
        } else if (Array.isArray(instances[0])) {
            return this.updateFlatInstances(instances);
        } else if (instances[0] instanceof this) {
            return this.updateClassInstances(instances);
        } else if (instances[0].id) {
            return this.updateMapInstances(instances);
        } else {
            throw new Error("You must pass an array of instances to the updateInstances method on " + this.toString() + ". The instances you passed were: " + JSON.stringify(instances));
        }
    }


    static fetch({client, params, cache, abortController, paginate = true}) {
        if (!paginate) {
            return this.dal(client).get(
                this.uri,
                {
                    params: {
                        ...params,
                        proj: this.columns.join(","),
                        flat: true,
                        limit: params?.limit || 1000000000,
                        _cb: 2
                    },
                    cache: cache
                },
                abortController
            ).then((response) => {
                if (response?.data?.results === undefined) {
                    throw new Error(
                        `Response data does not contain 'results' field. ` +
                        `Expected a list of ${this.pluralName} models, but got: ` +
                        !!data ? JSON.stringify(data) : "null or undefined"
                    );
                }
                if (response?.data?.results === null) {
                    return [];
                }
                return this.updateInstances(response.data.results);
            });
        }

        return this.dal(client).paginate(
            this.uri,
            {
                params: {
                    ...params,
                    proj: this.columns.join(","),
                    flat: true,
                    limit: params?.limit || 1000000000,
                    _cb: 2
                },
                cache: cache
            },
            abortController
        ).then((data) => {
            if (data === undefined) {
                throw new Error(
                    `Response data does not contain 'results' field. ` +
                    `Expected a list of ${this.pluralName} models, but got: ` +
                    !!data ? JSON.stringify(data) : "null or undefined"
                );
            }
            if (data === null) {
                return [];
            }
            return this.updateInstances(data);
        });
    }

    static fetchByDate({client, startDate, endDate, dateFormatter, abortController, ...overrides}) {
        const params = this._getParams({startDate, endDate, ...overrides, proj: this.columns.join(","), flat: true, limit: overrides?.limit || 1000000000});
        console.log("Fetching", this.name, "with params", params);
        return this.dal(client).paginateByDate(
            this.uri,
            {
                params,
                cache: overrides?.cache || true // TODO: Remove the true default.
            },
            startDate,
            endDate,
            this.dateField,
            abortController,
            dateFormatter,
        ).then((instances) => {
            return this.updateInstances(instances);
        });
    }

    static accountsToParam(accounts) {
        throw new Error("You must define accountsToParams on the model to convert accounts to params for the DAL request.");
    }

    static fetchById(client, id, abortController) {
        const localInstances = this.byId;
        const found = localInstances[id]
        if (found) {
            return Promise.resolve(found);
        }
        return this.dal(client).get(
            this.uri + id.toString() + "/",
            {integration: this.namespace},
            abortController,
        ).then((response) => {
            if (response === null || response === undefined) {
                return Promise.reject(null);
            }
            if (this.byId.get(response.data.id)) {
                return Promise.resolve([this.byId.get(response.data.id)]);
            }
            const instance = new this(response.data);
            return Promise.resolve(this.updateInstances([instance]));
        });
    }

    stringify() {
        const mapping = {};
        this.constructor.columns.map(column => {
            mapping[column] = this[column];
        });
        return JSON.stringify(mapping);
    }
}
